import { AxiosError } from "axios";
import { useGrowl } from "PFApp/use_growl";
import { Button } from "PFComponents/button";
import Card from "PFComponents/card/card";
import FolderTabs from "PFComponents/tabs/folder_tabs";
import InputFieldSet from "PFComponents/text/input_field_set";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useBookingConfigCalendarsInvalidate } from "PFCore/hooks/queries/bookings/booking_config/use_booking_config_calendars_invalidate";
import { useCreateCalendar } from "PFCore/hooks/queries/bookings/booking_config/use_create_calendar";
import React, { useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { CalendarForm } from "../calendars.types";
import { DEFAULT_CALENDAR_FORM_DAYS } from "../calendars.utils";
import BreadCrumbs from "../components/calendars/breadcrumbs";
import { Dates } from "../components/dates/dates";
import { CalendarIcon, EditIcon } from "../components/icons";
import css from "../edit_booking_calendar.module.scss";

export const NewBookingCalendar = () => {
  const { t } = useTranslation("bookingModule");
  const growl = useGrowl();
  const { getFirstError } = useResponseErrors();

  const DEFAULT_CALENDAR_FORM: CalendarForm = useMemo(
    () => ({
      name: t("config.calendars.newCalendar.newCalendarName"),
      ...DEFAULT_CALENDAR_FORM_DAYS
    }),
    [t]
  );

  const handleError = (error: AxiosError<Error>) => {
    try {
      const errorMessage = getFirstError(error.response?.data);
      growl({
        message: errorMessage,
        kind: "error"
      });
    } catch {
      growl({
        message: t("config.calendars.unknownError"),
        kind: "error"
      });
    }
  };

  const history = useHistory();
  const [tabId, setTabId] = useState("1");
  const methods = useForm<CalendarForm>({
    defaultValues: DEFAULT_CALENDAR_FORM
  });

  const { invalidate: invalidateCalendars } = useBookingConfigCalendarsInvalidate();
  const { mutate: createCalendar } = useCreateCalendar({
    options: {
      onSuccess: (calendarId) => {
        invalidateCalendars();
        growl({
          message: t("config.calendars.newCalendar.success"),
          kind: "success"
        });
        history.push(`/admin/booking/calendars/${calendarId}`);
      },
      onError: handleError
    }
  });

  const { handleSubmit, getValues } = methods;

  const [editMode, setEditMode] = useState(false);

  const toggleEditMode = () => setEditMode(!editMode);

  const onSubmit = (form: CalendarForm) => {
    createCalendar({
      calendarName: form.name,
      nominalDays: form.nominalDays,
      exceptionDays: form.exceptionDays,
      main: false
    });
  };

  const handleTabChange = (_, id: string) => setTabId(id);

  return (
    <>
      <BreadCrumbs currentName={getValues("name")} />
      <Card noPadding>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {editMode ? (
              <div style={{ display: "flex", alignItems: "center" }}>
                <div className={css.header}>
                  <Controller
                    name={"name"}
                    render={({ field }) => (
                      <InputFieldSet
                        inputType={"text"}
                        {...field}
                        handleChange={field.onChange}
                        handleBlur={field.onBlur}
                      />
                    )}
                  />
                </div>
                <Button kind="tertiary" onClick={toggleEditMode}>
                  {t("config.calendars.update")}
                </Button>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center" }}>
                <div className={css.header}>{getValues("name")}</div>
                <Button kind="tertiary" onClick={toggleEditMode}>
                  <EditIcon height={15} width={15} />
                </Button>
              </div>
            )}
            <FolderTabs
              onTabSelected={handleTabChange}
              tabs={[
                {
                  id: "1",
                  displayElement: (
                    <div className={css.tab}>
                      <div>
                        <CalendarIcon width={20} height={20} />
                      </div>
                      <div> {t("config.calendars.dates")}</div>
                    </div>
                  ),
                  Content: <Dates />
                }
              ]}
              selectedTabId={tabId}
            />
          </form>
        </FormProvider>
      </Card>
    </>
  );
};
